import { log } from '@ms-ows/logging'

import { DEFAULT_SURFACE_ID } from '../constants'
import {
  FabricDependencies,
  ICoachmark,
  ITeachingBubbleStyleProps,
  ITeachingBubbleStyles,
} from '../fabric-dependencies'

import { SurfaceProps } from './office-web-surface-container'
import { getTeachingBubbleProps } from './teaching'

const SURFACE_LOAD_TIME_MILLISECONDS = 1000

// tslint:disable-next-line: max-func-body-length
const getCoachmarkSurfaceBase = (fabricDependencies: FabricDependencies) => (
  props: SurfaceProps
) => {
  const {
    createElement: h,
    Coachmark,
    DirectionalHint,
    TeachingBubbleContent,
    useEffect,
    useRef,
  } = fabricDependencies

  const coachmarkRef = useRef<ICoachmark>()

  useEffect(() => {
    const setPopupSurfaceId = () => {
      try {
        // get the root element of the coachmark and set the id to ensure
        // keyboard tabbing works in WAC, setting other ways did not work as expected
        // tslint:disable-next-line: no-any no-unsafe-any
        const element = (coachmarkRef.current as any)?._entityHost
          ?.current as HTMLElement

        if (element && !element.id) {
          element.id = DEFAULT_SURFACE_ID
        }
      } catch (error) {
        log.error(
          0x23753189 /* tag_93tgj */,
          'error setting coachmark id',
          error
        )
      }
    }

    // set popup surface id after a delay to account for the initial render animation
    setTimeout(setPopupSurfaceId, SURFACE_LOAD_TIME_MILLISECONDS)
  }, [])

  const anchorHint = props.anchorHint || 'bottomCenter'

  return (
    <Coachmark
      ariaAlertText={props.alertAriaText || props.title}
      ariaDescribedByText={props.description}
      ariaLabelledByText={props.title}
      color={props.theme && props.theme.palette.themePrimary}
      // tslint:disable-next-line: no-any no-unsafe-any
      componentRef={coachmarkRef as any}
      isCollapsed={!props.focused}
      isPositionForced={false}
      onAnimationOpenEnd={props.onFocused}
      onDismiss={props.onCalloutDismiss}
      persistentBeak={true}
      positioningContainerProps={{
        directionalHint: DirectionalHint[anchorHint],
      }}
      preventDismissOnLostFocus={props.preventAutoDismiss ?? !props.focused}
      preventFocusOnMount={!props.focused}
      target={props.targetElement}
    >
      <TeachingBubbleContent
        {...getTeachingBubbleProps(fabricDependencies, props)}
        styles={props.styles}
      >
        {props.description}
      </TeachingBubbleContent>
    </Coachmark>
  )
}

export const getCoachmarkSurface = (fabricDependencies: FabricDependencies) =>
  fabricDependencies.styled<
    SurfaceProps,
    ITeachingBubbleStyleProps,
    ITeachingBubbleStyles
  >(getCoachmarkSurfaceBase(fabricDependencies), {}, undefined, {
    // azure styles don't style TeachingBubbleContent, so grab TeachingBubble style and set it manually
    scope: 'TeachingBubble',
  })
